import { forwardRef, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLaneChooserPhaserGame } from '@/features/laneChooser/hooks/useLaneChooserPhaserGame';
import { useLaneChooserTheme } from '@/features/laneChooser/theme';

import Button from '@/shared/components/button/Button';

import type { CSSProperties } from 'react';
import type {
	LaneChooserAnswerResolution,
	LaneChooserQuestionPayload,
	LaneChooserSoundEffect,
} from '@/features/laneChooser/game/types';

export type LaneChooserStageProps = {
	className?: string;
	style?: CSSProperties;
	laneLinesCount?: number;
	roundKey?: number | string;
	question?: LaneChooserQuestionPayload | null;
	gameStarted?: boolean;
	answerResolution?: LaneChooserAnswerResolution | null;
	onAnswerRequest?: (selectedIndex: number) => void;
	onRoundExitComplete?: () => void;
	onSoundEffect?: (effect: LaneChooserSoundEffect) => void;
	onInstructionComplete?: () => void;
	onInstructionTooltipChange?: (textKey: string | null) => void;
	onInstructionTooltipPositionChange?: (point: { x: number; y: number } | null) => void;
	onQuestionImagePreview?: (imageSrc: string) => void;
};

const LaneChooserStage = forwardRef<HTMLDivElement, LaneChooserStageProps>(
	(
		{
			className,
			style,
			laneLinesCount = 4,
			roundKey,
			question = null,
			gameStarted = false,
			answerResolution = null,
			onAnswerRequest,
			onRoundExitComplete,
			onSoundEffect,
			onInstructionComplete,
			onInstructionTooltipChange,
			onInstructionTooltipPositionChange,
			onQuestionImagePreview,
		},
		forwardedRef,
	) => {
		const { t } = useTranslation();
		const theme = useLaneChooserTheme();
		const containerRef = useRef<HTMLDivElement | null>(null);
		const [instructionsVisible, setInstructionsVisible] = useState(false);

		const setRefs = useCallback(
			(node: HTMLDivElement | null) => {
				containerRef.current = node;
				if (typeof forwardedRef === 'function') {
					forwardedRef(node);
					return;
				}
				if (forwardedRef) {
					forwardedRef.current = node;
				}
			},
			[forwardedRef],
		);

		const handleInstructionTooltipChange = useCallback(
			(textKey: string | null) => {
				setInstructionsVisible(Boolean(textKey));
				onInstructionTooltipChange?.(textKey);
			},
			[onInstructionTooltipChange],
		);

		const { skipInstructions } = useLaneChooserPhaserGame({
			containerRef,
			themeConfig: theme.game,
			laneLinesCount,
			roundKey,
			question,
			gameStarted,
			answerResolution,
			onAnswerRequest,
			onRoundExitComplete,
			onSoundEffect,
			onInstructionComplete,
			onInstructionTooltipChange: handleInstructionTooltipChange,
			onInstructionTooltipPositionChange,
			onQuestionImagePreview,
		});

		const combinedClassName = [className, 'lane-chooser__phaser-stage'].filter(Boolean).join(' ');

		const handleSkipInstructions = () => {
			setInstructionsVisible(false);
			skipInstructions();
		};

		return (
			<>
				<div className={combinedClassName} ref={setRefs} style={style} />
				{instructionsVisible && !gameStarted && (
					<div className="lane-chooser__instruction-skip">
						<Button
							type="primary"
							iconName="icon-check"
							title={t('lane_chooser.hints.skip')}
							onClick={handleSkipInstructions}
						/>
					</div>
				)}
			</>
		);
	},
);

LaneChooserStage.displayName = 'LaneChooserStage';

export default LaneChooserStage;
